import {
  Breadcrumb,
  Button,
  Col,
  Divider,
  Row,
  Table,
  message,
  Card,
  Spin,
  Tooltip,
} from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Back } from '../../Images/Back.svg';
import {
  fetchCustomerById,
  fetchCustomerDropdown,
  resetCurrentCustomerPage,
} from '../../slices/customer';
import { fetchConversionRateById } from '../../slices/conversion-rate';
import { fetchDieWiseWorkOrderReport, fetchWorkOrderReport } from '../../slices/work-order';
import { PrinterOutlined, FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import logo from '../../Images/logo.svg';

const ViewCustomer = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const printRef = useRef();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [dieOrderLoading, setDieOrderLoading] = useState(false);
  const [conversionData, setConversionData] = useState([]);
  const { user: currentUser } = useSelector(state => state.auth);
  const [dieWiseData, setDieWiseData] = useState([]);
  const [dieWiseTableData, setDieWiseTableData] = useState([]);
  const [page, setPage] = useState(1);
  const tableRef = useRef(null);
  const [pagesize, setPageSize] = useState(100);
  const [summaryData, setSummaryData] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await dispatch(fetchConversionRateById(id)).unwrap();
      if (response.results && response.results.data) {
        setConversionData(response.results.data);
      } else {
        message.error('No conversion rate data found.');
      }
    } catch (err) {
      message.error('Failed to fetch conversion rate data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, id]);

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        dispatch(fetchCustomerById(id))
          .unwrap()
          .then(response => {
            if (response) {
              setCustomer(response.data);
              fetchWorkOrderData(response.data.id);
            }
          })
          .catch(error => {
            console.error('Error fetching customer:', error);
            message.error('Failed to fetch customer details');
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        setLoading(false);
        message.error('An error occurred while fetching customer details');
      }
    };

    if (id) {
      fetchCustomer();
    }
  }, [id, dispatch]);

  const fetchDieWiseWorkOrderData = () => {
    setDieOrderLoading(true);

    dispatch(fetchDieWiseWorkOrderReport({ customer_id: id }))
      .unwrap()
      .then(response => {
        if (response && Array.isArray(response.data)) {
          // Flatten the die-wise data
          const flatRows = [];
          response.data.forEach(die => {
            if (Array.isArray(die.workorders) && die.workorders.length > 0) {
              die.workorders.forEach((wo, idx) => {
                flatRows.push({
                  ...wo,
                  die_number: die.die_number,
                  die_diagram: die.die_diagram,
                  die_rowSpan: idx === 0 ? die.workorders.length : 0,
                });
              });
            } else {
              // If no workorders, still show die info
              flatRows.push({
                die_number: die.die_number,
                die_diagram: die.die_diagram,
                die_rowSpan: 1,
              });
            }
          });
          setDieWiseData(flatRows);
          setDieWiseTableData(flatRows);
        } else {
          setDieWiseData([]);
          setDieWiseTableData([]);
        }
      })
      .catch((error) => {
        setDieWiseData([]);
        setDieWiseTableData([]);
        message.error(`Failed to fetch die wise work order report: ${error.message || 'Unknown error'}`);
      })
      .finally(() => setDieOrderLoading(false));
  };

  useEffect(() => {
    if (id) {
      fetchDieWiseWorkOrderData();
    }
  }, [id])

  const fetchWorkOrderData = (customerId = id) => {
    setOrderLoading(true);

    const params = {
      page,
      pagesize,
      customer_id: customerId,
    };

    dispatch(fetchWorkOrderReport(params))
      .then(response => {
        const mergedData = () => {
          const grouped = {};
          response?.payload?.data.forEach(item => {
            const key = `${item.workorder_no}-${item.po_no}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
          });

          const result = [];
          Object.values(grouped).forEach(group => {
            group.forEach((row, index) => {
              result.push({
                ...row,
                workorder_rowSpan: index === 0 ? group.length : 0,
                po_rowSpan: index === 0 ? group.length : 0,
              });
            });
          });

          return result;
        };
        let finalResult = mergedData;
        setOrderData(finalResult);
        setSummaryData(response?.payload?.total_summary);
        // setOrderData(response?.payload?.data || []);
      })
      .catch(error => {
        console.error('Error fetching work order report:', error);
        setOrderData([]);
        message.error('Failed to fetch work order report');
      })
      .finally(() => {
        setOrderLoading(false);
      });
  };

  const gstStatusMap = {
    igst: 'IGST',
    sgst_cgst: 'SGST_CGST',
  };

  const contactColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      render: (text, record, index) => index + 1,
      width: 80,
    },
    {
      title: 'Person Name',
      dataIndex: 'contact_person_name',
      key: 'contact_person_name',
    },
    {
      title: 'Designation',
      dataIndex: 'contact_person_designation',
      key: 'contact_person_designation',
    },
    {
      title: 'Email',
      dataIndex: 'contact_person_email',
      key: 'contact_person_email',
    },
    {
      title: 'Mobile',
      dataIndex: 'contact_person_mobile_number',
      key: 'contact_person_mobile_number',
    },
  ];

  const bankingColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      render: (text, record, index) => index + 1,
      width: 80,
    },
    {
      title: 'Bank Name',
      dataIndex: 'bank_name',
      key: 'bank_name',
    },
    {
      title: 'Bank Account Number',
      dataIndex: 'bank_account_number',
      key: 'bank_account_number',
    },
    {
      title: 'Bank IFSC Code',
      dataIndex: 'bank_ifsc_code',
      key: 'bank_ifsc_code',
    },
    {
      title: 'Branch Address',
      dataIndex: 'bank_branch_address',
      key: 'bank_branch_address',
    },
    {
      title: 'BENEFICIARY SWIFT CODE',
      dataIndex: 'beneficiary_swift_code',
      key: 'beneficiary_swift_code',
    },
    {
      title: 'Bank AD Code',
      dataIndex: 'bank_ad_code',
      key: 'bank_ad_code',
    },
  ];

  const workOrderColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'sr_no',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
      width: '60px',
    },
    {
      title: 'Work Order No / Date',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      width: '110px',
      render: (value, row) => ({
        children: value,
        props: {
          rowSpan: row.workorder_rowSpan,
        },
      }),
    },
    {
      title: 'PO NO / Date',
      dataIndex: 'po_no',
      key: 'po_no',
      width: '100px',
      render: (value, row) => ({
        children: value,
        props: {
          rowSpan: row.po_rowSpan,
        },
      }),
    },
    {
      title: 'Profile No',
      dataIndex: 'section',
      key: 'section',
      width: 80,
      align: 'center',
    },
    {
      title: 'Profile Image',
      dataIndex: 'profile_image',
      key: 'profile_image',
      width: 90,
      render: profile_image => {
        if (!profile_image?.startsWith('https://')) return;

        return (
          <img
            width="70"
            src={profile_image}
            alt="Profile Image"
            style={{ objectFit: 'contain', maxHeight: '100%' }}
          />
        );
      },
    },
    {
      title: 'Customer Ref No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: 90,
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      width: 90,
      align: 'center',
    },
    {
      title: 'Alloy / Temper',
      dataIndex: 'alloy_temper',
      key: 'alloy_temper',
      width: 90,
      align: 'center',
    },
    {
      title: 'Order',
      children: [
        {
          title: 'PCS',
          dataIndex: 'order_pcs',
          key: 'order_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'order_kgs',
          key: 'order_kgs',
          width: 90,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Packed',
      children: [
        {
          title: 'PCS',
          dataIndex: 'packed_pcs',
          key: 'packed_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'packed_kgs',
          key: 'packed_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Warehouse',
      children: [
        {
          title: 'PCS',
          dataIndex: 'warehouse_pcs',
          key: 'warehouse_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'warehouse_kgs',
          key: 'warehouse_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Dispatched',
      children: [
        {
          title: 'PCS',
          dataIndex: 'dispatched_pcs',
          key: 'dispatched_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'dispatched_kgs',
          key: 'dispatched_kgs',
          align: 'right',

          width: 80,
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Pending',
      children: [
        {
          title: 'PCS',
          dataIndex: 'pending_pcs',
          key: 'pending_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'pending_kgs',
          key: 'pending_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Remarks',
      dataIndex: 'remark',
      key: 'remark',
      align: 'center',
      width: 120,
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <span>{text || ''}</span>
        </Tooltip>
      ),
    },
  ];


  const DieWiseWorkOrderColumns = [
    {
      title: 'Sr. No.',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
      width: '60px',
    },
    {
      title: 'Profile No',
      dataIndex: 'die_number',
      key: 'die_number',
      width: 80,
      align: 'center',
      render: (value, row) => ({
        children: value,
        props: { rowSpan: row.die_rowSpan },
      }),
    },
    {
      title: 'Die Image',
      dataIndex: 'die_diagram',
      key: 'die_diagram',
      width: 90,
      render: (profile_image, row) => {
        if (!profile_image?.startsWith('https://')) return;
        return row.die_rowSpan ? (
          <img
            width="70"
            src={profile_image}
            alt="Die Image"
            style={{ objectFit: 'contain', maxHeight: '100%' }}
          />
        ) : null;
      },
    },
    {
      title: 'IWO No / Date',
      dataIndex: 'workorder_no',
      key: 'workorder_no',
      width: '110px',
    },
    {
      title: 'PO NO / Date',
      dataIndex: 'po_no',
      key: 'po_no',
      width: '100px',
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      width: 80,
      align: 'center',
    },
    {
      title: 'Customer Ref No',
      dataIndex: 'customer_reference_number',
      key: 'customer_reference_number',
      width: 90,
    },
    {
      title: 'Order',
      children: [
        {
          title: 'PCS',
          dataIndex: 'order_pcs',
          key: 'order_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'order_kgs',
          key: 'order_kgs',
          width: 90,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Packed',
      children: [
        {
          title: 'PCS',
          dataIndex: 'packed_pcs',
          key: 'packed_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'packed_kgs',
          key: 'packed_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Warehouse',
      children: [
        {
          title: 'PCS',
          dataIndex: 'warehouse_pcs',
          key: 'warehouse_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'warehouse_kgs',
          key: 'warehouse_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Dispatched',
      children: [
        {
          title: 'PCS',
          dataIndex: 'dispatched_pcs',
          key: 'dispatched_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'dispatched_kgs',
          key: 'dispatched_kgs',
          align: 'right',
          width: 80,
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Pending',
      children: [
        {
          title: 'PCS',
          dataIndex: 'pending_pcs',
          key: 'pending_pcs',
          width: 70,
          align: 'center',
          render: value => (value ? value : ''),
        },
        {
          title: 'KGS',
          dataIndex: 'pending_kgs',
          key: 'pending_kgs',
          width: 80,
          align: 'right',
          render: value => (value ? parseFloat(value).toFixed(3) : ''),
        },
      ],
    },
    {
      title: 'Remarks',
      dataIndex: 'remark',
      key: 'remark',
      align: 'center',
      width: 120,
      ellipsis: true,
      render: text => (
        <Tooltip title={text}>
          <span>{text || ''}</span>
        </Tooltip>
      ),
    },
  ];
  // Print and Excel logic for Die Wise Work Order
  const generateDieWiseTableRows = () => {
    return dieWiseTableData
      .map((row, index) => {
        const dieNoCell = row.die_rowSpan
          ? `<td rowspan="${row.die_rowSpan}">${row.die_number}</td>`
          : '';
        const dieImgCell = row.die_rowSpan && row.die_diagram && row.die_diagram.startsWith('https://')
          ? `<td rowspan="${row.die_rowSpan}"><img src="${row.die_diagram}" alt="Die" style="max-width:40px;max-height:30px;object-fit:contain;" onerror="this.style.display='none';" /></td>`
          : (row.die_rowSpan ? '<td rowspan="' + row.die_rowSpan + '"></td>' : '');
        return `
        <tr>
          <td>${index + 1}</td>
          ${dieNoCell}
          ${dieImgCell}
          <td>${row.workorder_no || ''}</td>
          <td>${row.po_no || ''}</td>
          <td>${row.length || ''}</td>
          <td>${row.customer_reference_number || ''}</td>
          <td>${row.order_pcs || ''}</td>
          <td style="text-align: right;">${row.order_kgs ? parseFloat(row.order_kgs).toFixed(3) : ''}</td>
          <td>${row.packed_pcs || ''}</td>
          <td style="text-align: right;">${row.packed_kgs ? parseFloat(row.packed_kgs).toFixed(3) : ''}</td>
          <td>${row.warehouse_pcs || ''}</td>
          <td style="text-align: right;">${row.warehouse_kgs ? parseFloat(row.warehouse_kgs).toFixed(3) : ''}</td>
          <td>${row.dispatched_pcs || ''}</td>
          <td style="text-align: right;">${row.dispatched_kgs ? parseFloat(row.dispatched_kgs).toFixed(3) : ''}</td>
          <td>${row.pending_pcs || ''}</td>
          <td style="text-align: right;">${row.pending_kgs ? parseFloat(row.pending_kgs).toFixed(3) : ''}</td>
          <td>${row.remark || ''}</td>
        </tr>
      `;
      })
      .join('');
  };


  const handleDieWisePrint = () => {
    const currentDateTime = new Date().toLocaleString();

    const printContent = `
    <table class="work-order-table">
      <thead>
        <tr>
          <th rowspan="2">Sr.<br/>No.</th>
          <th rowspan="2">Profile No</th>
          <th rowspan="2">Die Image</th>
          <th rowspan="2">IWO No/<br/>Date</th>
          <th rowspan="2">PO NO/<br/>Date</th>
          <th rowspan="2">Length<br/>(mm)</th>
          <th rowspan="2">Customer Ref No</th>
          <th colspan="2">Order</th>
          <th colspan="2">Packed</th>
          <th colspan="2">Warehouse</th>
          <th colspan="2">Dispatched</th>
          <th colspan="2">Pending</th>
          <th rowspan="2">Remarks</th>
        </tr>
        <tr>
          <th>PCS</th>
          <th>KGS</th>
          <th>PCS</th>
          <th>KGS</th>
          <th>PCS</th>
          <th>KGS</th>
          <th>PCS</th>
          <th>KGS</th>
          <th>PCS</th>
          <th>KGS</th>
        </tr>
      </thead>
      <tbody>
        ${generateDieWiseTableRows()}
      </tbody>
    </table>
  `;

    const windowPrint = window.open('', '', 'width=1200,height=900');
    windowPrint.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Die Wise Work Order Report</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 10px; color: #000; margin: 20px; }
          table { width: 100%; border-collapse: collapse; }
          table, th, td { border: 1px solid #333; }
          th, td { padding: 4px; text-align: center; font-size: 10px; }
          th { background: #eee; }
          @page { size: A4 landscape; margin: 20mm; }
          .work-order-table th, .work-order-table td { font-size: 9px; }
          .work-order-table img { max-width: 40px; max-height: 30px; object-fit: contain; }

          /* Header only on first page */
          .print-header {
            position: fixed;
            top: 0;
            right: 0;
            left: 0;
            text-align: right;
            padding: 10px 20px;
          }
          .print-header img { max-height: 50px; }
          .print-header { display: block; }

          @media print {
            body {
              counter-reset: page; /* 🔹 Reset page counter */
            }
            .print-header { display: block; }
            .print-header + h2 { margin-top: 60px; } /* push content down */

            /* Footer */
            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              font-size: 9px;
              display: flex;
              justify-content: space-between;
              padding: 4px 20px;
            }
            /* Page numbers */
            .print-footer::after {
              counter-increment: page;
              content: "Page " counter(page);
            }

            /* Hide logo after first page */
            .print-header { position: running(header); }
            @page:first {
              @top-right {
                content: element(header);
              }
            }
          }
        </style>
      </head>
      <body>
        <div class="print-header">
          <img src="${logo}" alt="Company Logo" />
        </div>

        <h2 style="text-align: center;">Die Wise Work Order Report</h2>
        <div>${printContent}</div>

        <div class="print-footer">
          <span>${currentDateTime}</span>
          <span></span>
        </div>
      </body>
    </html>
  `);

    windowPrint.document.close();
    windowPrint.focus();
    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 500);
  };



  const handleDieWiseExcelDownload = () => {
    const headers = [
      'Sr. No.', 'Profile No', 'IWO No / Date', 'PO No / Date', 'Length (mm)', 'Customer Ref No',
      'Order PCS', 'Order KGS', 'Packed PCS', 'Packed KGS', 'Warehouse PCS', 'Warehouse KGS',
      'Dispatched PCS', 'Dispatched KGS', 'Pending PCS', 'Pending KGS', 'Remarks'
    ];
    const dataRows = dieWiseTableData.map((row, index) => [
      index + 1,
      row.die_number,
      row.workorder_no,
      row.po_no,
      row.length,
      row.customer_reference_number,
      row.order_pcs,
      row.order_kgs,
      row.packed_pcs,
      row.packed_kgs,
      row.warehouse_pcs,
      row.warehouse_kgs,
      row.dispatched_pcs,
      row.dispatched_kgs,
      row.pending_pcs,
      row.pending_kgs,
      row.remark
    ]);

    // Create worksheet and merges for Profile No (Die No)
    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const merges = [];
    // Merge logic for Profile No (column B, index 1)
    let dieStartRow = 1; // Start from row 1 (after header)
    for (let i = 1; i < dataRows.length; i++) {
      const currentDie = dataRows[i][1];
      const previousDie = dataRows[i - 1][1];
      if (currentDie !== previousDie) {
        if (i - dieStartRow > 0) {
          merges.push({ s: { r: dieStartRow, c: 1 }, e: { r: i, c: 1 } });
        }
        dieStartRow = i + 1;
      }
    }
    // Handle the last group of Profile No
    if (dataRows.length - dieStartRow > 0) {
      merges.push({ s: { r: dieStartRow, c: 1 }, e: { r: dataRows.length, c: 1 } });
    }
    ws['!merges'] = merges;

    // Adjust column widths
    ws['!cols'] = [
      { wch: 10 }, // Sr. No.
      { wch: 15 }, // Profile No
      { wch: 20 }, // IWO No / Date
      { wch: 20 }, // PO No / Date
      { wch: 15 }, // Length (mm)
      { wch: 20 }, // Customer Ref No
      { wch: 10 }, // Order PCS
      { wch: 10 }, // Order KGS
      { wch: 10 }, // Packed PCS
      { wch: 10 }, // Packed KGS
      { wch: 10 }, // Warehouse PCS
      { wch: 10 }, // Warehouse KGS
      { wch: 10 }, // Dispatched PCS
      { wch: 10 }, // Dispatched KGS
      { wch: 10 }, // Pending PCS
      { wch: 10 }, // Pending KGS
      { wch: 15 }, // Remarks
    ];

    // Create a workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Die Wise Work Order Report');

    // Generate the Excel file and trigger download
    XLSX.writeFile(
      wb,
      `DieWiseWorkOrderReport.xlsx`,
    );
  };


  const handleConversionPrint = () => {
    const currentDateTime = new Date().toLocaleString();
    const printContent = tableRef.current.innerHTML;

    const windowPrint = window.open('', '', 'width=1200,height=900');
    windowPrint.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Conversion Rate Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            font-size: 20px;
            line-height: 1.2;
            color: #000;
            margin: 10px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          table, th, td {
            border: 1px solid #333;
          }
          th, td {
            padding: 4px;
            text-align: center;
            font-size: 20px;
          }
          th {
            background: #eee;
          }
          @page {
            size: A4 landscape;
            margin: 20mm;
          }

          /* Header only on first page */
          .print-header {
            position: fixed;
            top: 0;
            right: 0;
            left: 0;
            text-align: right;
            padding: 10px 20px;
          }
          .print-header img {
            max-height: 50px;
          }
          @media print {
            .print-header {
              display: block;
            }
            .print-header + h2 {
              margin-top: 60px; /* Push report title below header */
            }
            /* Footer */
            .print-footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              font-size: 14px;
              display: flex;
              justify-content: space-between;
              padding: 4px 20px;
            }
           .print-footer::after {
              counter-increment: page;
              content: "Page " counter(page);
            }

          }
        </style>
      </head>
      <body>
        <!-- Header (Logo only first page) -->
        <div class="print-header">
          <img src="${logo}" alt="Company Logo" />
        </div>

        <h2 style="text-align: center;">Conversion Rate Report</h2>
        <div>${printContent}</div>

        <!-- Footer -->
        <div class="print-footer">
          <span>${currentDateTime}</span>
          <span></span>
        </div>
      </body>
    </html>
  `);
    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 500);
  };


  const csvHeaders = [
    { label: 'Sr. No.', key: 'srNo' },
    { label: 'Profile', key: 'die' },
    { label: 'Alloy', key: 'alloy' },
    { label: 'Temper', key: 'temper' },
    { label: 'Conversion', key: 'conversion' },
    { label: 'Remarks', key: 'remarks' },
  ];

  const columns = [
    {
      title: 'Sr. No.',
      key: 'srNo',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Profile',
      dataIndex: 'die',
      key: 'die',
    },
    {
      title: 'Profile Image',
      dataIndex: 'die_diagram',
      key: 'die_diagram',
      render: (text, record) => (
        <img
          src={record.die_diagram}
          alt="Die Diagram"
          style={{ width: '100px', height: 'auto' }}
        />
      ),
    },
    {
      title: 'Alloy',
      dataIndex: 'alloy',
      key: 'alloy',
    },
    {
      title: 'Temper',
      dataIndex: 'temper',
      key: 'temper',
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion',
      key: 'conversion',
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (text) => text || '-',
    },
  ];

  const generateTableRows = () => {
    return orderData
      .map((order, index) => {
        const isWorkOrderRepeated =
          index > 0 && orderData[index - 1].workorder_no === order.workorder_no;
        const workOrderCell = isWorkOrderRepeated
          ? ''
          : `
        <td rowspan="${(() => {
            let rowSpan = 1;
            for (let i = index + 1; i < orderData.length; i++) {
              if (orderData[i].workorder_no === order.workorder_no) {
                rowSpan++;
              } else {
                break;
              }
            }
            return rowSpan;
          })()}">
          ${order.workorder_no}
          <br/>
          ${order.order_date ? dayjs(order.order_date).format('DD-MM-YYYY') : ''}
        </td>
      `;

        const isPoNoRepeated =
          index > 0 &&
          orderData[index - 1].workorder_no === order.workorder_no &&
          orderData[index - 1].po_no === order.po_no;
        const poNoCell = isPoNoRepeated
          ? ''
          : `
        <td rowspan="${(() => {
            let rowSpan = 1;
            for (let i = index + 1; i < orderData.length; i++) {
              if (
                orderData[i].workorder_no === order.workorder_no &&
                orderData[i].po_no === order.po_no
              ) {
                rowSpan++;
              } else {
                break;
              }
            }
            return rowSpan;
          })()}">
          ${order.po_no || ''}
          <br/>
          ${order.po_date ? dayjs(order.po_date).format('DD-MM-YYYY') : ''}
        </td>
      `;

        return `
        <tr>
          <td>${index + 1}</td>
          ${workOrderCell}
          ${poNoCell}
          <td>${order.section || 'N/A'}</td>
          <td>
            ${order.profile_image && order.profile_image !== 'N/A'
            ? `<img src="${order.profile_image}" alt="Profile" class="profile-image" onerror="this.style.display='none';" />`
            : ' '
          }
          </td>
          <td>${order.customer_reference_number || ''}</td>
          <td>${order.length || 'N/A'}</td>
          <td>${order.alloy_temper || 'N/A'}</td>
          <td>${order.order_pcs || ''}</td>
          <td style="text-align: right;">${order.order_kgs ? parseFloat(order.order_kgs).toFixed(3) : ''}</td>
          <td>${order.packed_pcs || ''}</td>
          <td style="text-align: right;">${order.packed_kgs ? parseFloat(order.packed_kgs).toFixed(3) : ''}</td>
          <td>${order.warehouse_pcs || ''}</td>
          <td style="text-align: right;">${order.warehouse_kgs ? parseFloat(order.warehouse_kgs).toFixed(3) : ''}</td>
          <td>${order.dispatched_pcs || ''}</td>
          <td style="text-align: right;">${order.dispatched_kgs ? parseFloat(order.dispatched_kgs).toFixed(3) : ''}</td>
          <td>${order.pending_pcs || ''}</td>
          <td style="text-align: right;">${order.pending_kgs ? parseFloat(order.pending_kgs).toFixed(3) : ''}</td>
          <td>${order.remark || ''}</td>
        </tr>
      `;
      })
      .join('');
  };


  const handlePrint = () => {
    const currentDateTime = new Date().toLocaleString();
    const windowPrint = window.open('', '', 'width=1200,height=900');

    windowPrint.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Work Order Report - ${customer?.customer_name || 'Customer'}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 9px;
            color: #333;
          }
          .print-container {
            padding: 5px;
          }
          .print-header {
            text-align: center;
            margin-bottom: 10px;
            padding: 5px 0;
            position: relative;
          }
          .print-header h1 {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 3px;
          }
          .print-header h2 {
            font-size: 14px;
            color: #666;
            margin: 0;
          }
          .logo {
            position: absolute;
            right: 0;
            top: 0;
            width: 100px;
          }
          .work-order-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8px;
            border: 0.5px solid #333;
          }
          .work-order-table th,
          .work-order-table td {
            border: 1px solid #333;
            padding: 2px 3px;
            text-align: center;
            vertical-align: middle;
          }
          .work-order-table th {
            background-color: #12406a;
            color: white;
            font-weight: bold;
          }
          .work-order-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .profile-image {
            max-width: 40px;
            max-height: 30px;
            object-fit: contain;
          }
          .total-row {
            font-weight: bold;
          }

          /* Footer for date/time and page numbers */
          @page {
            size: A4 portrait;
            margin: 0.7in 0.5in 0.8in 0.5in; /* top, right, bottom, left */
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .footer {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              font-size: 10px;
              display: flex;
              justify-content: space-between;
              padding: 5px 20px;
              background: white;
              color: #333;
            }
            .footer .datetime {
              display: block;
            }
            .footer .page-number:after {
             counter-increment: page;
              content: "Page " counter(page);
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="print-header">
            <h1>Work Order Report</h1>
            <h2>${customer?.customer_name || 'N/A'}</h2>
            <img src="${logo}" alt="Logo" class="logo" />
          </div>
          
          <table class="work-order-table">
            <thead>
              <tr>
                <th rowspan="2">Sr.<br/>No.</th>
                <th rowspan="2">Work Order No/<br/>Date</th>
                <th rowspan="2">PO NO/<br/>Date</th>
                <th rowspan="2">Profile<br/>No</th>
                <th rowspan="2">Profile<br/>Image</th>
                <th rowspan="2">Customer Ref<br/>No</th>
                <th rowspan="2">Length<br/>(mm)</th>
                <th rowspan="2">Alloy<br/>Temper</th>
                <th colspan="2">Order</th>
                <th colspan="2">Packed</th>
                <th colspan="2">Warehouse</th>
                <th colspan="2">Dispatched</th>
                <th colspan="2">Pending</th>
                <th rowspan="2">Remarks</th>
              </tr>
              <tr>
                <th>PCS</th><th>KGS</th>
                <th>PCS</th><th>KGS</th>
                <th>PCS</th><th>KGS</th>
                <th>PCS</th><th>KGS</th>
                <th>PCS</th><th>KGS</th>
              </tr>
            </thead>
            <tbody>
              ${generateTableRows()}
              ${orderData.length > 0 ? `
                <tr class="total-row">
                  <td colspan="8">Total</td>
                  <td>${orderData.reduce((s, o) => s + (+o.order_pcs || 0), 0)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.order_kgs || 0), 0).toFixed(3)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.packed_pcs || 0), 0)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.packed_kgs || 0), 0).toFixed(3)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.warehouse_pcs || 0), 0)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.warehouse_kgs || 0), 0).toFixed(3)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.dispatched_pcs || 0), 0)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.dispatched_kgs || 0), 0).toFixed(3)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.pending_pcs || 0), 0)}</td>
                  <td>${orderData.reduce((s, o) => s + (+o.pending_kgs || 0), 0).toFixed(3)}</td>
                  <td></td>
                </tr>
              ` : ''}
            </tbody>
          </table>
        </div>

        <div class="footer">
          <div class="datetime">Generated: ${currentDateTime}</div>
          <div class="page-number"></div>
        </div>
      </body>
    </html>
  `);

    windowPrint.document.close();
    windowPrint.focus();

    setTimeout(() => {
      windowPrint.print();
      windowPrint.close();
    }, 500);
  };


  const handleExcelDownload = () => {
    const headers = [
      'Sr. No.',
      'Work Order No / Date',
      'PO No / Date',
      'Profile No',
      'Customer Reference Number',
      'Length (mm)',
      'Alloy / Temper',
      'Order PCS',
      'Order KGS',
      'Packed PCS',
      'Packed KGS',
      'Warehouse PCS',
      'Warehouse KGS',
      'Dispatched PCS',
      'Dispatched KGS',
      'Pending PCS',
      'Pending KGS',
      'Remarks',
    ];

    // Prepare the data rows
    const dataRows = orderData.map((order, index) => {
      const workOrderCell = `${order.workorder_no || ''}${order.order_date ? '\n' + dayjs(order.order_date).format('DD-MM-YYYY') : ''}`;
      const poNoCell = `${order.po_no || ''}${order.po_date ? '\n' + dayjs(order.po_date).format('DD-MM-YYYY') : ''}`;

      return [
        index + 1,
        workOrderCell,
        poNoCell,
        order.section || 'N/A',
        order.customer_reference_number || '',
        order.length || 'N/A',
        order?.alloy_temper,
        order.order_pcs || '',
        order.order_kgs ? parseFloat(order.order_kgs) : '',
        order.packed_pcs || '',
        order.packed_kgs ? parseFloat(order.packed_kgs) : '',
        order.warehouse_pcs || '',
        order.warehouse_kgs ? parseFloat(order.warehouse_kgs) : '',
        order.dispatched_pcs || '',
        order.dispatched_kgs ? parseFloat(order.dispatched_kgs) : '',
        order.pending_pcs || '',
        order.pending_kgs ? parseFloat(order.pending_kgs) : '',
        order.remark || '',
      ];
    });

    const totalRow = [
      'TOTAL',
      '', // Work Order No / Date
      '', // PO No / Date
      '', // Profile No
      '', // Customer Reference Number
      '', // Length (mm)
      '',
      { f: `SUM(H2:H${dataRows.length + 1})` }, // Order PCS
      { f: `SUM(I2:I${dataRows.length + 1})` }, // Order KGS
      { f: `SUM(J2:J${dataRows.length + 1})` }, // Packed PCS
      { f: `SUM(K2:K${dataRows.length + 1})` }, // Packed KGS
      { f: `SUM(L2:L${dataRows.length + 1})` }, // Warehouse PCS                              
      { f: `SUM(M2:M${dataRows.length + 1})` }, // Warehouse KGS
      { f: `SUM(N2:N${dataRows.length + 1})` }, // Dispatched PCS
      { f: `SUM(O2:O${dataRows.length + 1})` }, // Dispatched KGS
      { f: `SUM(P2:P${dataRows.length + 1})` }, // Pending PCS
      { f: `SUM(Q2:Q${dataRows.length + 1})` }, // Pending KGS
      '', // Remarks
    ];
    // Combine headers, data rows, and total row
    const wsData = [headers, ...dataRows, totalRow];

    // Create a new workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Define merges array for cell merging
    const merges = [];

    // Group and merge logic for "Work Order No / Date" (column B, index 1)
    let workOrderStartRow = 1; // Start from row 1 (after header)
    for (let i = 1; i < dataRows.length; i++) {
      const currentWorkOrder = dataRows[i][1];
      const previousWorkOrder = dataRows[i - 1][1];
      if (currentWorkOrder !== previousWorkOrder) {
        if (i - workOrderStartRow > 0) {
          merges.push({
            s: { r: workOrderStartRow, c: 1 }, // Start row and column (B)
            e: { r: i, c: 1 }, // End row and column (B)
          });
        }
        workOrderStartRow = i + 1;
      }
    }
    // Handle the last group of Work Order
    if (dataRows.length - workOrderStartRow > 0) {
      merges.push({
        s: { r: workOrderStartRow, c: 1 },
        e: { r: dataRows.length, c: 1 },
      });
    }

    // Group and merge logic for "PO No / Date" (column C, index 2)
    let poNoStartRow = 1;
    for (let i = 1; i < dataRows.length; i++) {
      const currentPoNo = dataRows[i][2];
      const previousPoNo = dataRows[i - 1][2];
      const currentWorkOrder = dataRows[i][1];
      const previousWorkOrder = dataRows[i - 1][1];
      if (
        currentPoNo !== previousPoNo ||
        currentWorkOrder !== previousWorkOrder
      ) {
        if (i - poNoStartRow > 0) {
          merges.push({
            s: { r: poNoStartRow, c: 2 }, // Start row and column (C)
            e: { r: i, c: 2 }, // End row and column (C)
          });
        }
        poNoStartRow = i + 1;
      }
    }
    if (dataRows.length - poNoStartRow > 0) {
      merges.push({
        s: { r: poNoStartRow, c: 2 },
        e: { r: dataRows.length, c: 2 },
      });
    }

    ws['!merges'] = merges;
    ['I', 'K', 'M', 'O', 'Q'].forEach(col => {
      for (let row = 2; row <= dataRows.length + 2; row++) {
        const cellAddress = `${col}${row}`;
        if (ws[cellAddress]) {
          ws[cellAddress].z = '0.000'; // 3 decimal places
        }
      }
    });
    // Adjust column widths
    ws['!cols'] = [
      { wch: 10 }, // Sr. No.
      { wch: 20 }, // Work Order No / Date
      { wch: 20 }, // PO No / Date
      { wch: 15 }, // Profile No
      { wch: 20 }, // Customer Reference Number
      { wch: 15 }, // Length (mm)
      { wch: 15 },
      { wch: 10 }, // Order PCS
      { wch: 10 }, // Order KGS
      { wch: 10 }, // Dispatched PCS
      { wch: 10 }, // Dispatched KGS
      { wch: 10 }, // Packed PCS
      { wch: 10 }, // Packed KGS
      { wch: 10 }, // Warehouse PCS
      { wch: 10 }, // Warehouse KGS
      { wch: 10 }, // Pending PCS
      { wch: 10 }, // Pending KGS
      { wch: 15 }, // Remarks
    ];

    for (let row = 0; row < wsData.length; row++) {
      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;

        const isNumericColumn = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15].includes(
          col,
        );
        ws[cellAddress].s = {
          alignment: {
            vertical: 'center',
            horizontal: isNumericColumn ? 'right' : 'left',
            wrapText: true,
          },
        };
      }
    }

    // Create a workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Work Order Report');

    // Generate the Excel file and trigger download
    XLSX.writeFile(
      wb,
      `Work_Order_Report_${customer?.customer_name || 'Customer'}_${dayjs().format('DD-MM-YYYY')}.xlsx`,
    );
  };


  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="sticyaction">
        <Row
          className="mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2 style={{ margin: 0 }}>View Customer</h2>
            <div className="bredcrumbwrp">
              <Link to="/customer-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: <Link to="/customer-list">Customers</Link> },
                  { title: 'View Customer' },
                ]}
              />
            </div>
          </Col>
          <div style={{ display: 'flex', gap: '10px' }}>
            {currentUser.permissions.permissions.customer.includes(
              'change_customer',
            ) && (
                <Button
                  type="primary"
                  style={{ marginBottom: '20px' }}
                  onClick={() => navigate(`/edit-customer/${customer?.id}`)}
                >
                  Edit Customer
                </Button>
              )}
          </div>
        </Row>
      </div>

      <div className="px-4">
        <Card
          title="Customer Information"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Row gutter={[24, 16]} className="viewform">
            {customer?.business_type === 'INDIAN' && (
              <>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">GSTIN Number</label>
                  <div className="">{customer?.gstin_number || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">GST Type</label>
                  <div className="">{customer?.gst_type || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Pan Number</label>
                  <div className="">{customer?.pan_number || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Udyam No / MSME</label>
                  <div className="">{customer?.udyam_no || 'N/A'}</div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Applicable GST</label>
                  <div className="">
                    {customer?.applicable_gst
                      ? gstStatusMap[customer?.applicable_gst] || 'Unknown'
                      : 'N/A'}
                  </div>
                </Col>
              </>
            )}
            {customer?.business_type === 'OVERSEAS' && (
              <>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Import Export Code</label>
                  <div className="">
                    {customer?.import_export_code || 'N/A'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={8} xl={6}>
                  <label className="customlabel">Beneficiary Agent Code</label>
                  <div className="">
                    {customer?.beneficiary_agent_code || 'N/A'}
                  </div>
                </Col>
              </>
            )}
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer Name</label>
              <div className="">{customer?.customer_name || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Email</label>
              <div className="">{customer?.email || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Phone</label>
              <div className="">+{customer?.phone_number || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Fax Number</label>
              <div className="">{customer?.fax_number || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Website</label>
              <div className="">{customer?.website || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer Type</label>
              <div className="">{customer?.customer_type?.name || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Delivery Days</label>
              <div className="">{customer?.delivery_days || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer Profile No</label>
              <div className="">
                {customer?.customer_section_no !== null
                  ? customer?.customer_section_no
                  : 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Sales Executive</label>
              <div className="">
                {customer?.sales_executive?.first_name || 'N/A'}
              </div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Sales Executive Assistant</label>
              <div className="">
                {customer?.sales_executive_assistant?.first_name || 'N/A'}
              </div>
            </Col>
          </Row>
        </Card>
        <Card
          title="Address Information"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Row gutter={[24, 16]} className="viewform">
            <Col xs={24} md={12}>
              <label className="customlabel">Office Address</label>
              <div className="">
                {`${customer?.office_address_shop || ''} 
                  ${customer?.office_address_area || ''} 
                  ${customer?.office_address_landmark || ''} 
                  ${customer?.office_address_city || ''} 
                  ${customer?.office_address_state || ''} 
                  ${customer?.office_address_country || ''} 
                  - ${customer?.office_address_pin_code || ''}`
                  .trim()
                  .toUpperCase()}
              </div>
            </Col>
            <Col xs={24} md={12}>
              <label className="customlabel">Factory Address</label>
              <div className="">
                {`${customer?.factory_address_shop || ''} 
                  ${customer?.factory_address_area || ''} 
                  ${customer?.factory_address_landmark || ''} 
                  ${customer?.factory_address_city || ''} 
                  ${customer?.factory_address_state || ''} 
                  ${customer?.factory_address_country || ''} 
                  - ${customer?.factory_address_pin_code || ''}`
                  .trim()
                  .toUpperCase()}
              </div>
            </Col>
          </Row>
        </Card>
        <Card
          title="Bank Details"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Table
            dataSource={customer?.customer_banking_details?.filter(
              bank =>
                bank.bank_name ||
                bank.bank_account_number ||
                bank.bank_ifsc_code ||
                bank.bank_branch_address ||
                bank.beneficiary_swift_code ||
                bank.bank_ad_code,
            )}
            columns={bankingColumns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
        <Card
          title="Contact Persons"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Table
            dataSource={customer?.customer_contact_persons}
            columns={contactColumns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>
        <Card
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Work Order Report</span>
              <div>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Print
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExcelDownload}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Excel
                </Button>
                <Button
                  type="primary"
                  onClick={() => fetchWorkOrderData()}
                  loading={orderLoading}
                >
                  Refresh
                </Button>
              </div>
            </div>
          }
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          {orderLoading ? (
            <div style={{ padding: '50px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '10px' }}>Loading work order data...</p>
            </div>
          ) : orderData.length > 0 ? (
            <Table
              dataSource={orderData}
              columns={workOrderColumns}
              pagination={false}
              scroll={{ y: 'calc(100vh - 400px)' }}
              rowKey={(record) => record?.id}
              bordered
              summary={() => {
                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ fontWeight: 'bold' }}>
                      <Table.Summary.Cell index={0} colSpan={8}>
                        Total
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="center">
                        {summaryData?.order_pcs}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        {summaryData?.order_kgs?.toFixed(3)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="center">
                        {summaryData?.packed_pcs}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        {summaryData?.packed_kgs?.toFixed(3)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="center">
                        {summaryData?.warehouse_pcs}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="right">
                        {summaryData?.warehouse_kgs?.toFixed(3)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12} align="center">
                        {summaryData?.dispatched_pcs}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13} align="right">
                        {summaryData?.dispatched_kgs?.toFixed(3)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={14} align="center">
                        {summaryData?.pending_pcs}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={15} align="right">
                        {summaryData?.pending_kgs?.toFixed(3)}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={16}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>No work order data found for this customer.</p>
            </div>
          )}
        </Card>
        <Card
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Conversion Rate Details</span>
              <div>
                <Button
                  icon={<PrinterOutlined />}
                  disabled={!conversionData.length}
                  onClick={handleConversionPrint}
                  style={{ marginRight: '8px' }}
                >
                  Print
                </Button>

                <CSVLink
                  data={conversionData.map((row, index) => ({
                    srNo: index + 1,
                    ...row,
                  }))}
                  headers={csvHeaders}
                  filename={`conversion_rate_${id}.csv`}
                  style={{ marginRight: '8px' }}
                >
                  <Button
                    icon={<FileExcelOutlined />}
                    disabled={!conversionData.length}
                  >
                    Excel
                  </Button>
                </CSVLink>
              </div>
            </div>
          }
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Spin spinning={loading}>
            <div ref={tableRef}>
              <Table
                columns={columns}
                dataSource={conversionData}
                pagination={false}
                bordered
                style={{ marginTop: '16px' }}
                rowKey={(record) => record?.id}
              />
            </div>
          </Spin>
        </Card>
        <Card
          title={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Profile Wise Work Order Report </span>
              <div>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handleDieWisePrint}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Print
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleDieWiseExcelDownload}
                  disabled={!orderData.length}
                  style={{ marginRight: '8px' }}
                >
                  Excel
                </Button>
                <Button
                  type="primary"
                  onClick={() => fetchDieWiseWorkOrderData()}
                >
                  Refresh
                </Button>
              </div>
            </div>
          }
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          {orderLoading ? (
            <div style={{ padding: '50px' }}>
              <Spin size="large" />
              <p style={{ marginTop: '10px' }}>Loading work order data...</p>
            </div>
          ) : orderData.length > 0 ? (
            <Table
              dataSource={dieWiseData}
              columns={DieWiseWorkOrderColumns}
              pagination={false}
              scroll={{ y: 'calc(100vh - 400px)' }}
              rowKey={(record) => record?.id}
              bordered

            />
          ) : (
            <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <p>No work order data found for this customer.</p>
            </div>
          )}
        </Card>
      </div>
      <div ref={printRef} style={{ display: 'none' }}></div>

      <style jsx>{`
        .customlabel {
          font-weight: 600;
          color: #555;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          display: block;
        }

        . {
          font-size: 14px;
          color: #333;
          font-weight: 500;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #007bff;
          min-height: 36px;
          display: flex;
          align-items: center;
        }

        .viewform .ant-col {
          margin-bottom: 16px;
        }

        .sticyaction {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 16px 24px;
          border-bottom: 1px solid #e8e8e8;
          margin-bottom: 20px;
        }

        .pagenamerow h2 {
          color: #1f2937;
          font-size: 24px;
          font-weight: 600;
        }

        .bredcrumbwrp {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }

        .back {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          text-decoration: none;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .back:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .ant-card {
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .ant-card-head {
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-card-head-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-table-tbody > tr > td {
          padding: 12px 16px;
        }

        .ant-table-tbody > tr:hover > td {
          background: #f8fafc;
        }

        .ant-btn {
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .ant-btn-primary {
          background: #2563eb;
          border-color: #2563eb;
        }

        .ant-btn-primary:hover {
          background: #1d4ed8;
          border-color: #1d4ed8;
        }

        @media (max-width: 768px) {
          .sticyaction {
            padding: 12px 16px;
          }

          .px-4 {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          . {
            font-size: 13px;
            padding: 6px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewCustomer;
